import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal, BadgeCheck, AlertCircle } from 'lucide-react';
import { useEmployees } from '@/composables/payroll/useEmployees';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import { EMPLOYEE_STATUS_OPTIONS, EMPLOYMENT_TYPE_OPTIONS, type Employee } from '@/types/payroll';

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  ON_LEAVE: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  RESIGNED: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
  TERMINATED: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

export function EmployeeListPage() {
  const navigate = useNavigate();
  const { employees, meta, isLoading, filters, deleteEmployee, exportCsv } = useEmployees();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);

  const columns: ColumnDef<Employee, unknown>[] = useMemo(() => [
    {
      accessorKey: 'employeeCode',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.employeeCode}</span>,
    },
    {
      accessorKey: 'firstName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.firstName} {row.original.lastName}</p>
          {row.original.designation && (
            <p className="text-xs text-muted-foreground">{row.original.designation}</p>
          )}
        </div>
      ),
    },
    { accessorKey: 'department', header: 'Department', cell: ({ row }) => row.original.department || '-' },
    {
      accessorKey: 'employmentType',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-sm">
          {EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === row.original.employmentType)?.label ?? row.original.employmentType}
        </span>
      ),
    },
    {
      id: 'ctc',
      header: 'Current CTC',
      cell: ({ row }) => {
        const structure = row.original.salaryStructures?.[0];
        if (!structure) {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3 w-3" />not set
            </span>
          );
        }
        return <span className="tabular-nums">{formatCurrency(structure.ctcAnnual, structure.currency)}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant="outline" className={`border-0 font-medium ${STATUS_STYLES[row.original.status] ?? ''}`}>
          {row.original.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'dateOfJoining',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Joined" />,
      cell: ({ row }) => formatDateUTC(row.original.dateOfJoining),
    },
    {
      id: 'selfService',
      header: 'Login',
      cell: ({ row }) => row.original.user
        ? <BadgeCheck className="h-4 w-4 text-emerald-500" aria-label="Linked to a login" />
        : <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/people/employees/${row.original.id}`); }}>View</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/people/employees/${row.original.id}/edit`); }}>Edit</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer
      title="Employees"
      description="People on payroll — including contractors and interns without a login"
      actions={
        <>
          <Button variant="outline" disabled={isExporting} onClick={() => exportFile(() => exportCsv(), 'employees.csv')}>
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
          <Button onClick={() => navigate('/people/employees/new')}>
            <Plus className="mr-2 h-4 w-4" />New Employee
          </Button>
        </>
      }
    >
      <DataTableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, code, email…"
        filters={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.current.status || 'all'} onValueChange={(v) => filters.set({ status: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {EMPLOYEE_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.current.employmentType || 'all'} onValueChange={(v) => filters.set({ employmentType: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {EMPLOYMENT_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={employees}
        isLoading={isLoading}
        page={meta?.page ?? 1}
        totalPages={meta?.totalPages ?? 1}
        total={meta?.total ?? 0}
        pageSize={filters.current.limit}
        onPageChange={(p) => filters.set({ page: p })}
        onPageSizeChange={(s) => filters.set({ limit: s })}
        onRowClick={(row) => navigate(`/people/employees/${row.id}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Employee"
        description={`${deleteTarget?.firstName} ${deleteTarget?.lastName} (${deleteTarget?.employeeCode}) will be removed, along with their salary structures. This cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteEmployee.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteEmployee.isPending}
      />
    </PageContainer>
  );
}
